'use client'

import type { VariantProps } from 'class-variance-authority'
import type { HoverCard as HoverCardPrimitive } from 'radix-ui'

import * as React from 'react'
import { cva } from 'class-variance-authority'
import { formatDistance } from 'date-fns'
import { Slot as SlotPrimitive } from 'radix-ui'

import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from '@/shared/components/ui/hover-card'
import { cn } from '@/shared/lib/utils'

type HoverCardProps = React.ComponentProps<typeof HoverCardPrimitive.Root>
type HoverCardContentProps = React.ComponentProps<
	typeof HoverCardPrimitive.Content
>

function formatRelativeTime(date: Date): string {
	const now = new Date()
	return formatDistance(date, now, { addSuffix: true })
}

interface TimezoneCardProps extends React.ComponentProps<'div'> {
	date: Date
	locale?: string
	timezone?: string
}

export function TimezoneCard(props: TimezoneCardProps) {
	const { date, timezone, locale: _locale, ...cardProps } = props

	const locale = React.useMemo(
		() => Intl.DateTimeFormat(_locale).resolvedOptions().locale,
		[_locale]
	)

	const timezoneName = React.useMemo(
		() =>
			timezone ??
			new Intl.DateTimeFormat(locale, { timeZoneName: 'shortOffset' })
				.formatToParts(date)
				.find((part) => part.type === 'timeZoneName')?.value,
		[date, timezone, locale]
	)

	const { formattedDate, formattedTime } = React.useMemo(
		() => ({
			formattedDate: new Intl.DateTimeFormat(locale, {
				month: 'long',
				day: 'numeric',
				year: 'numeric',
				timeZone: timezone,
			}).format(date),
			formattedTime: new Intl.DateTimeFormat(locale, {
				hour: '2-digit',
				minute: '2-digit',
				second: '2-digit',
				timeZone: timezone,
			}).format(date),
		}),
		[date, timezone, locale]
	)

	return (
		<div
			role='region'
			aria-label={`Time in ${timezoneName}: ${formattedDate} ${formattedTime}`}
			{...cardProps}
			className='flex items-center justify-between gap-2 text-xs text-muted-foreground'
		>
			<span className='w-fit rounded bg-accent px-1 text-xs font-medium'>
				{timezoneName}
			</span>
			<div className='flex items-center gap-2'>
				<time className='text-foreground' dateTime={date.toISOString()}>
					{formattedDate}
				</time>
				<time className='tabular-nums' dateTime={date.toISOString()}>
					{formattedTime}
				</time>
			</div>
		</div>
	)
}

const triggerVariants = cva(
	'inline-flex w-fit items-center justify-center text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
	{
		variants: {
			variant: {
				default: '',
				muted: 'text-foreground/50 hover:text-foreground/70',
				ghost: 'hover:underline',
			},
		},
		defaultVariants: {
			variant: 'default',
		},
	}
)

interface RelativeTimeCardProps
	extends
		React.ComponentProps<'button'>,
		HoverCardProps,
		Pick<
			HoverCardContentProps,
			| 'align'
			| 'side'
			| 'alignOffset'
			| 'sideOffset'
			| 'avoidCollisions'
			| 'collisionBoundary'
			| 'collisionPadding'
			| 'asChild'
		>,
		VariantProps<typeof triggerVariants> {
	date: Date | string | number
	timezones?: string[]
	updateInterval?: number
	locale?: string
}

function RelativeTimeCard(props: RelativeTimeCardProps) {
	const {
		date: dateProp,
		variant,
		timezones = ['UTC'],
		open,
		defaultOpen,
		onOpenChange,
		openDelay = 500,
		closeDelay = 300,
		align,
		side,
		alignOffset,
		sideOffset,
		avoidCollisions,
		collisionBoundary,
		collisionPadding,
		updateInterval = 1000,
		asChild,
		children,
		className,
		locale: _locale,
		...triggerProps
	} = props

	const date = React.useMemo(
		() => (dateProp instanceof Date ? dateProp : new Date(dateProp)),
		[dateProp]
	)

	const locale = React.useMemo(
		() => Intl.DateTimeFormat(_locale).resolvedOptions().locale,
		[_locale]
	)

	const [formattedTime, setFormattedTime] = React.useState<string>(() =>
		date.toLocaleDateString()
	)

	React.useEffect(() => {
		setFormattedTime(formatRelativeTime(date))
		const timer = setInterval(() => {
			setFormattedTime(formatRelativeTime(date))
		}, updateInterval)

		return () => clearInterval(timer)
	}, [date, updateInterval])

	const TriggerPrimitive = asChild ? SlotPrimitive.Slot : 'button'

	return (
		<HoverCard
			open={open}
			defaultOpen={defaultOpen}
			onOpenChange={onOpenChange}
			openDelay={openDelay}
			closeDelay={closeDelay}
		>
			<HoverCardTrigger asChild>
				<TriggerPrimitive
					{...triggerProps}
					className={cn(triggerVariants({ variant, className }))}
				>
					{children ?? (
						<time dateTime={date.toISOString()} suppressHydrationWarning>
							{new Intl.DateTimeFormat(locale, {
								month: 'short',
								day: 'numeric',
								year: 'numeric',
								hour: '2-digit',
								minute: '2-digit',
							}).format(date)}
						</time>
					)}
				</TriggerPrimitive>
			</HoverCardTrigger>
			<HoverCardContent
				side={side}
				align={align}
				sideOffset={sideOffset}
				alignOffset={alignOffset}
				avoidCollisions={avoidCollisions}
				collisionBoundary={collisionBoundary}
				collisionPadding={collisionPadding}
				className='flex w-full max-w-[420px] flex-col gap-2 p-3'
			>
				<time
					dateTime={date.toISOString()}
					className='text-xs text-muted-foreground'
				>
					{formattedTime}
				</time>
				<div role='list' className='flex flex-col gap-1'>
					{timezones.map((timezone) => (
						<TimezoneCard
							key={timezone}
							role='listitem'
							locale={locale}
							date={date}
							timezone={timezone}
						/>
					))}
					<TimezoneCard locale={locale} role='listitem' date={date} />
				</div>
			</HoverCardContent>
		</HoverCard>
	)
}

export { RelativeTimeCard }
